
import Vue from "vue";
import { Chart } from "highcharts-vue";
import { Options } from "highcharts";
import { GroupListGraphSeries, Metric } from "@/api/entities";

// radial primary green 濃淡 (棒グラフ用)
const BAR_COLORS = ["#366564", "#519998", "#5FB3B1", "#A5D4D4", "#CCE4E4", "#E7F4F5"];
// radial info 濃淡 (折れ線グラフ・左軸用)
const LINE_INFO_COLORS = ["#264984", "#3A76B6", "#4487C8", "#5EA3DA", "#76B3E0", "#BFDDF2"];
// radial primary gray 濃淡 (折れ線グラフ・右軸用)
const LINE_GRAY_COLORS = ["#3C5E66", "#5B8D99", "#7DA8B2", "#9FC4CC", "#CAE0E5", "#DFEFF2"];

const METRIC_KEY_MAP: Record<string, Metric> = {
  saleAmount: "salesResult",
  saleShare: "salesShare",
  grossProfitRate: "grossProfitRate",
  digestRate: "digestionRate",
};

type SelectedMetric = {
  dataset: number;
  chartType: "bar" | "line";
  key: string;
};

export default Vue.extend({
  name: "group-list-chart",
  components: { Chart },
  props: {
    graphData: { type: Object, default: null },
    dataFormat: { type: Number, required: true },
    timeUnit: { type: String, default: "day" },
  },
  data() {
    return {
      dataFormatIndex: 0,
      timeUnitIndex: "day",
      showOthers: true,
      dataSelectionDialog: false,
      selectedMetrics: [
        { dataset: 1, chartType: "bar", key: "saleAmount" },
        { dataset: 2, chartType: "line", key: "digestRate" },
      ] as SelectedMetric[],
      tempSelectedMetrics: [] as SelectedMetric[],
      resizeObserver: null as ResizeObserver | null,
      chartTypes: [
        { key: "bar", name: "棒グラフ" },
        { key: "line", name: "折れ線グラフ" },
      ],
      metricOptions: [
        { name: "売上", key: "saleAmount", isVolume: true, isPercent: false },
        { name: "売上シェア", key: "saleShare", isVolume: false, isPercent: true },
        { name: "粗利率", key: "grossProfitRate", isVolume: false, isPercent: true },
        { name: "消化率", key: "digestRate", isVolume: false, isPercent: true },
      ],
    };
  },
  watch: {
    dataFormat: {
      immediate: true,
      handler(val) {
        this.dataFormatIndex = val;
      },
    },
    dataFormatIndex(val) {
      this.$emit("update:dataFormat", val);
    },
    timeUnit: {
      immediate: true,
      handler(val) {
        this.timeUnitIndex = val;
      },
    },
    timeUnitIndex(val) {
      this.$emit("update:timeUnit", val);
    },
  },
  computed: {
    isMaxSelected(): boolean {
      return this.tempSelectedMetrics.length >= 2;
    },
    chartTitle(): string {
      return this.selectedMetrics
        .map((selected) => this.metricOptions.find((m) => m.key === selected.key)?.name)
        .filter(Boolean)
        .join(" / ");
    },
    leftLegendItems(): { name: string; color: string }[] {
      const selected1 = this.selectedMetrics.find((m) => m.dataset === 1) ?? null;
      if (!selected1 || !this.graphData) return [];
      const m1ApiKey = (METRIC_KEY_MAP[selected1.key] ?? selected1.key) as Metric;
      const rawSeries = this.graphData.series;
      const seriesArr: GroupListGraphSeries[] = rawSeries[0]?.[m1ApiKey] ?? [];
      const colors = this.getColorsForSeries(1, selected1.chartType);
      const nonOthers = seriesArr.filter((s: any) => !s.isOthers);
      const items = nonOthers.map((s: any, i: number) => ({
        name: s.name,
        color: colors[Math.min(i, colors.length - 1)],
      }));
      const others = seriesArr.find((s: any) => s.isOthers);
      if (this.showOthers && others) {
        items.push({ name: others.name || "その他", color: colors[Math.min(nonOthers.length, colors.length - 1)] });
      }
      return items;
    },
    rightLegendItems(): { name: string; color: string }[] {
      const selected2 = this.selectedMetrics.find((m) => m.dataset === 2) ?? null;
      if (!selected2 || !this.graphData) return [];
      const m2ApiKey = (METRIC_KEY_MAP[selected2.key] ?? selected2.key) as Metric;
      const rawSeries = this.graphData.series;
      const seriesArr: GroupListGraphSeries[] = rawSeries[0]?.[m2ApiKey] ?? [];
      const colors = this.getColorsForSeries(2, selected2.chartType);
      const nonOthers = seriesArr.filter((s: any) => !s.isOthers);
      const items = nonOthers.map((s: any, i: number) => ({
        name: s.name,
        color: colors[Math.min(i, colors.length - 1)],
      }));
      const others = seriesArr.find((s: any) => s.isOthers);
      if (this.showOthers && others) {
        items.push({ name: others.name || "その他", color: colors[Math.min(nonOthers.length, colors.length - 1)] });
      }
      return items;
    },
    chartOptions(): Options {
      const categories: string[] = this.graphData?.categories ?? [];
      const count = categories.length;

      const m1 = this.selectedMetrics.find((m) => m.dataset === 1) ?? null;
      const m2 = this.selectedMetrics.find((m) => m.dataset === 2) ?? null;
      const m1Obj = m1 ? this.metricOptions.find((m) => m.key === m1.key) ?? null : null;
      const m2Obj = m2 ? this.metricOptions.find((m) => m.key === m2.key) ?? null : null;
      const m1IsYen = !!(m1Obj?.isVolume && this.dataFormatIndex === 1);
      const m2IsYen = !!(m2Obj?.isVolume && this.dataFormatIndex === 1);
      const m1IsPercent = !!m1Obj?.isPercent;
      const m2IsPercent = !!m2Obj?.isPercent;

      const getDataArray = (series: any): { y: number; isOriginalNull: boolean }[] => {
        if (!series.metrics) return [];
        const dataKey = this.dataFormatIndex === 1 ? "amount" : "quantity";
        return (series.metrics[dataKey] ?? []).map((v: number | null) => ({ y: v ?? 0, isOriginalNull: v === null }));
      };

      const rawSeries = this.graphData?.series ?? [];
      const m1ApiKey = m1 ? ((METRIC_KEY_MAP[m1.key] ?? m1.key) as Metric) : null;
      const m2ApiKey = m2 ? ((METRIC_KEY_MAP[m2.key] ?? m2.key) as Metric) : null;
      const seriesForM1: GroupListGraphSeries[] = m1ApiKey ? rawSeries[0]?.[m1ApiKey] ?? [] : [];
      const seriesForM2: GroupListGraphSeries[] = m2ApiKey ? rawSeries[0]?.[m2ApiKey] ?? [] : [];

      const seriesData: any[] = [];
      let m1HasNegative = false;
      let m2HasNegative = false;

      if (m1 && m1Obj) {
        const isBar = m1.chartType === "bar";
        const colors1 = this.getColorsForSeries(1, m1.chartType);
        seriesForM1.forEach((s: any, idx: number) => {
          if (!this.showOthers && s.isOthers) return;
          const data = getDataArray(s);
          if (data.some((v) => v.y < 0)) m1HasNegative = true;
          seriesData.push({
            name: `${s.name} (${m1Obj.name})`,
            type: isBar ? "column" : "line",
            stack: "m1",
            yAxis: 0,
            color: colors1[Math.min(idx, colors1.length - 1)],
            isYen: m1IsYen,
            isPercent: m1IsPercent,
            data,
            zIndex: isBar ? 1 : 10,
          });
        });
      }

      if (m2 && m2Obj) {
        const isBar2 = m2.chartType === "bar";
        const colors2 = this.getColorsForSeries(2, m2.chartType);
        seriesForM2.forEach((s: any, idx: number) => {
          if (!this.showOthers && s.isOthers) return;
          const data = getDataArray(s);
          if (data.some((v) => v.y < 0)) m2HasNegative = true;
          seriesData.push({
            name: `${s.name} (${m2Obj.name})`,
            type: isBar2 ? "column" : "line",
            stack: "m2",
            yAxis: 1,
            color: colors2[Math.min(idx, colors2.length - 1)],
            isYen: m2IsYen,
            isPercent: m2IsPercent,
            data,
            zIndex: isBar2 ? 1 : 10,
          });
        });
      }

      return {
        chart: { backgroundColor: "#fbfbfb", marginBottom: 110, animation: false },
        title: { text: "" },
        xAxis: {
          categories,
          labels: { style: { color: "#799498" } },
          tickInterval: 1,
        },
        yAxis: [
          {
            title: { text: m1Obj ? m1Obj.name : "", style: { color: "#799498", fontWeight: "bold" } },
            gridLineColor: "#DCDCDC",
            min: m1HasNegative ? undefined : 0,
            labels: {
              formatter: function () {
                const val = Number(this.value);
                if (m1IsYen) return "¥" + val.toLocaleString();
                if (m1IsPercent) return val.toLocaleString() + "%";
                return val.toLocaleString();
              },
            },
          },
          {
            title: { text: m2Obj ? m2Obj.name : "", style: { color: "#799498", fontWeight: "bold" } },
            opposite: true,
            gridLineWidth: 0,
            min: m2HasNegative ? undefined : 0,
            labels: {
              formatter: function () {
                const val = Number(this.value);
                if (m2IsYen) return "¥" + val.toLocaleString();
                if (m2IsPercent) return val.toLocaleString() + "%";
                return val.toLocaleString();
              },
            },
          },
        ],
        plotOptions: {
          column: {
            stacking: "normal",
            borderWidth: 0,
            groupPadding: count > 20 ? 0.1 : 0.2,
            pointPadding: 0,
          },
          line: {
            marker: { enabled: count < 20 },
            lineWidth: 4,
          },
          series: { animation: false },
        },
        tooltip: {
          shared: false,
          useHTML: true,
          formatter: function () {
            const options = this.series.options as { color?: string; isYen?: boolean; isPercent?: boolean };
            const isYen = options.isYen;
            const isPercent = options.isPercent;
            const isOriginalNull = (this.point as any).isOriginalNull;
            const val = isOriginalNull
              ? "∞"
              : isYen
              ? "¥" + (this.y ?? 0).toLocaleString()
              : isPercent
              ? (this.y ?? 0).toLocaleString() + "%"
              : (this.y ?? 0).toLocaleString();
            const header = `<b>${this.key}</b><br/>`;
            const color = options.color;
            return header + `<span style="color:${color}">●</span> ${this.series.name}: <b>${val}</b>`;
          },
        },
        series: seriesData,
        legend: { enabled: false },
        credits: { enabled: false },
      };
    },
    selectedCount() {
      return this.tempSelectedMetrics.length;
    },
  },
  mounted() {
    const observer = new ResizeObserver(() => {
      this.$nextTick(() => {
        const chart = (this.$refs.chart as InstanceType<typeof Chart>)?.chart;
        if (chart) chart.reflow();
      });
    });
    observer.observe(this.$el);
    this.resizeObserver = observer;
  },
  beforeDestroy() {
    this.resizeObserver?.disconnect();
  },
  methods: {
    getColorsForSeries(dataset: number, chartType: "bar" | "line"): string[] {
      if (chartType === "bar") return BAR_COLORS;
      return dataset === 1 ? LINE_INFO_COLORS : LINE_GRAY_COLORS;
    },
    openDataSelection() {
      this.tempSelectedMetrics = this.selectedMetrics.map((item) => ({ ...item }));
      this.dataSelectionDialog = true;
    },
    filteredMetricOptions(chartType) {
      if (chartType === "bar") {
        return this.metricOptions.filter((item) => item.key === "saleAmount");
      }
      return this.metricOptions;
    },
    isSelected(dataset, chartType, key) {
      return this.tempSelectedMetrics.some(
        (item) => item.dataset === dataset && item.chartType === chartType && item.key === key
      );
    },
    isMetricUsedByOtherDataset(dataset, key) {
      return this.tempSelectedMetrics.some((item) => item.dataset !== dataset && item.key === key);
    },
    toggleMetric(dataset, chartType, key) {
      if (this.isMetricDisabled(dataset, chartType, key)) return;

      const index = this.tempSelectedMetrics.findIndex(
        (item) => item.dataset === dataset && item.chartType === chartType && item.key === key
      );

      if (index >= 0) {
        this.tempSelectedMetrics.splice(index, 1);
        return;
      }

      this.tempSelectedMetrics.push({
        dataset,
        chartType,
        key,
      });
    },
    applyMetrics() {
      this.selectedMetrics = this.tempSelectedMetrics.map((item) => ({ ...item }));
      this.dataSelectionDialog = false;
      const apiMetrics = [...this.selectedMetrics]
        .sort((a, b) => a.dataset - b.dataset)
        .map((m) => METRIC_KEY_MAP[m.key] ?? m.key);
      this.$emit("metrics-change", apiMetrics);
    },
    isMetricDisabled(dataset, chartType, key) {
      const selected = this.tempSelectedMetrics.some(
        (item) => item.dataset === dataset && item.chartType === chartType && item.key === key
      );

      const datasetAlreadySelected = this.tempSelectedMetrics.some((item) => item.dataset === dataset);

      return (
        !selected &&
        (this.selectedCount >= 2 ||
          datasetAlreadySelected ||
          this.isMetricUsedByOtherDataset(dataset, key) ||
          this.isChartTypeUsed(chartType, key))
      );
    },
    isChartTypeUsed(chartType, key) {
      if (chartType !== "bar") return false;
      return this.tempSelectedMetrics.some((item) => item.chartType === "bar" && item.key !== key);
    },
  },
});
